import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { HttpErrorResponse } from '@angular/common/http'
import {
  CellClassParams,
  CellClickedEvent,
  CellStyle,
  ColDef,
  ColumnApi,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  Module,
  RowClassParams,
  RowNode,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  getSelectedForContextMenu,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
  setFilter,
} from '@helios/ag-grid-config'
import {
  AggInfoFilterModalService,
  ColumnTypeOverride,
  ColumnTypeOverrideMap,
  ColumnTypeOverrideService,
  FilterModalService,
  isColumnTypeOverride,
} from '@helios/filter-modal'

import {
  AdvanceControl,
  createPanelSettingsToolPanel,
  HighlightingService,
} from '@helios/generic-components'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { AgGridState } from '@northfork/ag-grid-angular'
import { ToastService } from '@northfork/ng-basics'
import { downloadExcelFile } from '@helios/utils'
import { merge, of, ReplaySubject, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { WorkbenchGridContext } from '../../models/grid-context.model'
import {
  ColumnCatalogEntry,
  SourceDefinition,
} from '../../models/source-definition.model'
import {
  GridRole,
  MatchTargetKey,
  otherRole,
  RowOrigin,
  RPS_SOURCE,
  SourceKey,
  TARGET_META,
} from '../../models/source-key.model'
import { PositionActionKey } from '../../models/position-action.model'
import { WorkbenchRow } from '../../models/workbench-row.model'
import {
  isSourceSelectable,
  SOURCE_REGISTRY,
} from '../../registry/source-registry'
import { calcUnitValue } from '../../registry/unit-difference'
import { buildColDefs, colSourceMap } from '../../grid/col-def-factory'
import {
  AGG_INFO_FILTER,
  AggInfoColumnFilterComponent,
} from '../../grid/filters/agg-info-column-filter.component'

import { buildFieldMeta, QueryFieldMeta } from '../../grid/query/field-types'
import {
  buildQueryRequest,
  GridSortModelEntry,
} from '../../grid/query/query-request.builder'
import {
  buildExportRequest,
  sortModelFromColumnState,
} from '../../grid/query/export-request.builder'
import { AdaptedCatalog, adaptCatalog } from '../../grid/field-catalog.adapter'
import { FieldCatalogService } from '../../services/field-catalog.service'
import {
  classifyQueryError,
  invalidFieldTypeMessage,
  QueryFailure,
} from '../../services/query-error'

import { createWorkbenchColumnManagerToolPanel } from '../column-manager/workbench-column-manager.sidebar-utils'
import {
  externalFilterActive,
  passesExternalFilter,
  QuickFilterState,
  WorkbenchExternalFilterModel,
} from '../../grid/filters/external-filters'
import {
  chipStateFromFilterModel,
  MATCH_STATUS_FIELD,
  MatchStatusChipState,
  matchStatusFilterModel,
  readMatchStatusFilterModel,
} from '../../grid/filters/match-status-chips'
import {
  workbenchStatusBarPanel,
  WorkbenchStatusBarModule,
} from '../../grid/status-bar/workbench-status-bar.module'
import { Positions2Key } from '../../../panel-key'
import { AggregateInfoService } from '../../services/aggregate-info.service'
import { MatchingService } from '../../services/matching.service'
import { PositionsTaskService } from '../../services/positions-task.service'
import { PositionsExportService } from '../../services/positions-export.service'
import { WorkbenchDataSource } from '../../services/workbench-data-source'
import { WorkbenchStoreService } from '../../services/workbench-store.service'
import { positionActionErrorMessage } from '../../utils/action-error'
import { buildPositionActionRequest } from '../../utils/action-request'
import { matchAdditionalApplies } from '../../utils/available-actions'
import { buildContextMenuItems } from '../../utils/context-menu'
import { planUnmatch, UnmatchPlan } from '../../utils/unmatch-plan'
import {
  UnmatchModalComponent,
  UnmatchSourceOption,
} from '../unmatch-modal/unmatch-modal.component'

interface SourceOption {
  key: SourceKey
  label: string
  /** Short transport/model tag shown beside the label (EXT / CORE / API). */
  tag: string
  /** Accent for the tag — blue for the Helios-side feeds, purple for the Enfusion API. */
  tagColor: string
  /** Listed but not choosable — a pairing the workbench is not cleared to show yet. */
  disabled: boolean
}

const MATCH_COUNT_PREFIX = 'matchCount:'

/**
 * Rows per /query block.
 *
 * 1000 is the house figure for a Helios server-side grid — both peer grids override the shared
 * `heliosServerSideRowConfig` default of 50 to exactly this (`external-transactions-grid` :688,
 * `clearwater-transactions-grid` :921), because 50-row blocks turn a scroll through a large account
 * into hundreds of round trips.
 */
const CACHE_BLOCK_SIZE = 1000

/**
 * How many blocks stay resident. 5 × 1000 = the 5000 rows the old single fixed page held, so the
 * client-side facilities that read the LOADED rows — cross-grid matching, the unit-difference
 * columns, the status-bar totals — keep at least today's reach rather than silently shrinking to the
 * shared default's 3 × 1000.
 *
 * Beyond that, blocks are evicted and refetched on scroll, which is the point: the grid is no longer
 * CAPPED at 5000, it just keeps 5000 in memory.
 */
const MAX_BLOCKS_IN_CACHE = 5

/** Why Quick Search is unavailable on a server-side source. Shown as the input's tooltip. */
const QUICK_SEARCH_DISABLED_REASON =
  'Search is unavailable while positions are filtered on the server — it would only match the rows already loaded. Use a column filter or the Filters modal instead.'

/**
 * Why the Ignored chip is disabled, on a catalog that doesn't support it. Shown as the checkbox's
 * tooltip only in that case — see {@link WorkbenchGridComponent.ignoredChipDisabledReason}.
 *
 * A catalog whose `matchStatus` entry has not yet added `IGNORED` to `allowedValues` (PVAL-26415)
 * carries no value representing "ignored", so there is nothing for this chip to filter on. A disabled
 * control with a stated reason is the honest presentation; a live checkbox that silently changes
 * nothing is not.
 */
const IGNORED_CHIP_DISABLED_REASON =
  'Ignoring a position is not represented in Flex Positions data yet (PVAL-26415), so this filter has nothing to match on.'

/**
 * Fallback when there is nothing to adapt — no focused account, or the fetch failed. The grid still
 * renders its computed columns, and the Column Manager recovers their Helios section from them.
 */
const EMPTY_CATALOG: AdaptedCatalog = { columns: [], groupOrder: [] }

/**
 * Toast copy for "Export to Excel" (PVAL-26421). The in-progress / success / failure strings are the
 * house wording Positions 1.0 already shows for its server-side export, so the two panels read alike.
 * Exported for the spec.
 */
export const EXPORT_MESSAGES = {
  inProgress:
    'Export to Excel is in progress. It may take more than 30 seconds. Please wait.',
  inProgressTitle: 'In Progress',
  success:
    "Export to Excel completed successfully. The exported file is available in your browser's Downloads",
  failed: 'Export to Excel failed. Please try again.',
  /** Clearwater (no export endpoint), or no displayed column the backend can export. */
  nothingToExport: 'There are no exportable columns to export.',
} as const

const EXPORT_TOAST_CONFIG = { closeButton: true, timeOut: 3000 }

/** The Helios ReportableError (StandardApiError) envelope, of which we only need the errorId here.
 * Defined locally to avoid a cross-lib dependency on @helios/messaging just for one optional field. */
interface ReportableErrorEnvelope {
  errorId?: string
}

/**
 * Pull the `errorId` out of a failed catalog request. Helios API errors carry a StandardApiError
 * envelope on `HttpErrorResponse.error`; bug reports are keyed on its `errorId`, so it is surfaced
 * on both catalog-error states. Returns null when the failure carried no such envelope (e.g. a
 * transport-level failure with no response body).
 */
const extractErrorId = (err: unknown): string | null => {
  if (!(err instanceof HttpErrorResponse)) return null
  const envelope = err.error as ReportableErrorEnvelope | null | undefined
  return envelope?.errorId ?? null
}

/**
 * The workspace saved-view key for one grid's column layout, sort and filters (PVAL-26418).
 *
 * Scoped by role AND source: the same role shows a different column set per source, so one view per
 * role would restore one source's layout onto another's catalog. Deliberately distinct from the
 * Filter Groups / Filter Modal key (`${Positions2Key}-${role}Grid`), which stays per role.
 */
export const savedViewGridKey = (role: GridRole, source: SourceKey): string =>
  `${Positions2Key}-${role}-${source}Grid`

/** Whether two column-type maps hold the same type for the same fields. */
const sameColumnTypes = (
  a: ColumnTypeOverrideMap,
  b: ColumnTypeOverrideMap,
): boolean => {
  const fields = Object.keys(a)
  return (
    fields.length === Object.keys(b).length &&
    fields.every(field => a[field] === b[field])
  )
}

/**
 * The Panel Settings persisted alongside each saved view. Never empty: the shared saved-view service
 * only saves filter state for a view whose panelSettings are non-empty, and reads
 * `clearFiltersOnAccountLoad` by that literal name.
 */
export interface WorkbenchPanelSettings {
  [key: string]: unknown
  clearFiltersOnAccountLoad: boolean
  sortByAbsolute: boolean
  showClosedPositions: boolean
  /**
   * The AGGREGATE_INFO comparison types the user picked for this view's source (field -> type), so
   * a reload does not put every retyped column back to Text. Kept here rather than in the filter
   * state, which is dropped while Clear Filters on Load is on — the type is not a filter.
   */
  columnTypeOverrides: ColumnTypeOverrideMap
}

/**
 * The source-agnostic positions grid. One instance per role (Comparison / Base); it reads its
 * current source from the shared {@link WorkbenchStoreService}, builds column defs from that
 * source's catalog, and routes selection / cross-filter through the store. All cross-grid
 * coordination happens via the store — this component never references its sibling.
 */
@Component({
  selector: 'hs-positions-workbench-grid',
  templateUrl: './workbench-grid.component.html',
  styleUrls: ['./workbench-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // FilterModalService is deliberately grid-scoped — each of the two workbench grids registers its
  // own ag-Grid instance with it. The AggInfo subclass is substituted for it here (and ONLY here) so
  // this panel's AGGREGATE_INFO columns, whose comparison type the user owns, still appear in the
  // Filters modal; every other consumer of the shared modal keeps the base builder unchanged.
  providers: [
    HighlightingService,
    { provide: FilterModalService, useClass: AggInfoFilterModalService },
  ],
})
export class WorkbenchGridComponent implements OnInit, OnDestroy {
  @Input() role!: GridRole

  private readonly destroy$ = new Subject<void>()

  /** Pushes when the user clicks Retry on a retryable (5xx) catalog error — re-drives the fetch. */
  private readonly retry$ = new Subject<void>()

  readonly gridModules: Module[] = [
    MenuModule,
    SetFilterModule,
    RangeSelectionModule,
    FiltersToolPanelModule,
    ServerSideRowModelModule,
    WorkbenchStatusBarModule,
  ]

  gridApi?: GridApi
  /** Captured on grid-ready; the projection reads the displayed columns off it. */
  private columnApi?: ColumnApi
  gridOptions?: GridOptions

  sourceKey!: SourceKey
  source!: SourceDefinition
  roleLabel = ''
  sourceOptions: SourceOption[] = []

  // Reactive controls throughout so the Northfork form components (nf-checkbox, nf-form-control) are
  // driven the same way as every other Helios grid header.
  readonly quickSearch = new FormControl('')
  readonly includeUnmatchedFormControl = new FormControl(true)
  /**
   * Disabled by default, until the first catalog resolution tells us whether this account's backend
   * supports it — see {@link updateIgnoredChipAvailability}. Constructed disabled rather than hidden
   * so the filter stays discoverable while unsupported, and so nothing can enable it by accident: an
   * un-updated backend has no `matchStatus` value behind it, and inventing one would send a predicate
   * that matches nothing while reading as "no ignored positions".
   */
  readonly includeIgnoredFormControl = new FormControl({
    value: false,
    disabled: true,
  })
  readonly includeMatchedFormControl = new FormControl(false)
  readonly differencesOnlyToggle = new FormControl(false)

  // Panel Settings (the cog in the side rail). These hold their state; wiring the behaviour to the
  // grid comes with the real data, but the toggle has to persist either way.
  readonly clearFiltersOnLoadToggle = new FormControl(true)
  readonly sortByAbsoluteValueToggle = new FormControl(false)
  /** Clearwater only — see {@link SourceDefinition.hasClosedPositionsSetting}. */
  readonly showClosedPositionsToggle = new FormControl(false)

  filterCount = 0
  clearAllFiltersEnabled = false

  private relatedGroups = new Set<string>()
  private crossFilterIds: string[] | null = null
  /** Guards the chip <-> matchStatus sync against re-entering itself. */
  private syncingChips = false

  /**
   * A source switch has reset the toolbar and is waiting for the new catalog before its default chip
   * state is written into the filter model — see {@link applyPendingSourceDefaults}. Exists so that
   * one user action produces exactly one /query, under the correct catalog.
   */
  private pendingSourceDefaults = false
  /**
   * Whether THIS catalog's `matchStatus` entry lists `IGNORED` under `allowedValues` (PVAL-26415).
   * Re-derived on every catalog resolution (source/account switch) rather than assumed fixed, since
   * different sources/accounts can be on different backend versions. Drives both whether the Ignored
   * chip is enabled and whether {@link matchStatusFilterModel} is allowed to ever emit `IGNORED`.
   */
  private ignoredSupported = false
  private colDefs: ColDef[] = []
  /** The current source's resolved catalog columns (empty until the fetch lands). */
  private catalogColumns: ColumnCatalogEntry[] = []
  /** Column Manager section order for the current source, straight from the catalog's groups. */
  private catalogGroupOrder: string[] = []

  /** Column-pipeline state, surfaced to the template for loading / error / empty affordances. */
  columnsLoading = false
  /**
   * How the catalog fetch failed, which decides which strip the template renders (work item 13):
   *  - `'not-retryable'`: a 404 — RPS has no Enfusion metadata for this account's CW client.
   *    Retrying the same request will keep 404ing, so no Retry is offered; the sibling grid (e.g.
   *    Prometheus) still renders from its own request.
   *  - `'retryable'`: a 5xx (or a status-0 transport failure) — RPS or business-ws is unreachable,
   *    which is transient, so the user is offered a Retry that refetches this grid's catalog.
   *  - `'none'`: no error.
   *
   * A 400 is a developer error (missing accountId / unparseable sources) that should never reach a
   * user — the fetch is skipped entirely without an account, so it cannot be produced here — and a
   * 401 is handled upstream by the standard auth interceptor's redirect. Both fall into
   * `'retryable'` if they ever do surface, which still shows the errorId.
   */
  columnsErrorKind: 'none' | 'not-retryable' | 'retryable' = 'none'
  /**
   * The `errorId` from the Helios ReportableError (StandardApiError) envelope, surfaced on BOTH
   * error states because bug reports are keyed on it. Null when the response carried no envelope.
   */
  columnsErrorId: string | null = null
  columnsEmpty = false

  /** True whenever the catalog fetch failed, either kind — keeps the empty strip mutually
   * exclusive with the error strips without the template having to enumerate both kinds. */
  get columnsError(): boolean {
    return this.columnsErrorKind !== 'none'
  }

  /**
   * Row-pipeline state.
   *
   * `rowsLoading` deliberately does NOT drive the full-area mask that `columnsLoading` does. By the
   * time rows are being fetched the columns are already built from the fields API, so the headers
   * should stay on screen and only the grid body shows a spinner — ag-Grid's own loading overlay,
   * raised in {@link fetchRows}. Masking the whole area instead reads as "the grid never loaded".
   */
  rowsLoading = false

  /**
   * An "Export to Excel" request is in flight. A server-side export of up to 300,000 rows can take
   * well over 30 seconds, so a second click while one runs is ignored rather than queuing a second,
   * identical workbook. Cleared however the request settles (success, failure or teardown).
   */
  exportInProgress = false

  /**
   * How the LAST /query call failed, or null. Three kinds have to read differently (they are three
   * different user situations, and two of them are not "something is broken"):
   *  - `invalid-field-type`: a chosen comparison type Postgres could not apply. Names the column(s)
   *    and the offending value, and offers Reset to Text — the only kind with a targeted action.
   *  - `session-expired`: a 410. Nothing is broken and a Retry cannot help, so it must be
   *    distinguishable from a 5xx AND from a legitimately empty result.
   *  - `unknown`: transient; offers a Retry.
   */
  queryFailure: QueryFailure | null = null

  /** Rows the grid currently holds, keyed by block start row — see {@link absorbBlock}. */
  private readonly loadedBlocks = new Map<number, WorkbenchRow[]>()

  get rowsError(): boolean {
    return this.queryFailure != null
  }

  get invalidFieldTypeMessage(): string {
    return this.queryFailure ? invalidFieldTypeMessage(this.queryFailure) : ''
  }

  /** Quick Search is unavailable on the server-side sources — see {@link QUICK_SEARCH_DISABLED_REASON}. */
  get quickSearchDisabledReason(): string | null {
    return this.source?.hasServerSideQuery ? QUICK_SEARCH_DISABLED_REASON : null
  }

  /** See {@link IGNORED_CHIP_DISABLED_REASON}. `null` once the catalog reports Ignored as supported,
   * so the tooltip stops claiming a limitation that no longer applies. */
  get ignoredChipDisabledReason(): string | null {
    return this.ignoredSupported ? null : IGNORED_CHIP_DISABLED_REASON
  }

  /**
   * Whether the last dispatched matching action was refused — either by `/flex-positions/actions`
   * or by the client, before the request (see {@link dispatch}).
   *
   * The user-facing surfacing is a toast, raised at the point of failure; this flag is what the
   * specs assert on and what a future inline affordance would bind to. Reset at the start of every
   * dispatch so it describes the LAST action, not any action.
   */
  actionError = false

  /** Mutated in place — the status panels read it through `gridOptions.context`. */
  private readonly gridContext: WorkbenchGridContext = {
    rangeSumEnabled: true,
  }

  /**
   * Releases the CURRENT workspace saved-view binding (PVAL-26418), or null while unbound.
   *
   * One subject per binding, and a ReplaySubject rather than a plain Subject: the shared service
   * subscribes to it with `takeUntil` only after an `await`, and removes its listeners off it with a
   * later subscription too. A plain Subject that already fired would be missed by both, so a bind
   * still resolving when its source was switched away would restore the old source's view onto the
   * new catalog and leak its listeners. A ReplaySubject that has emitted short-circuits both.
   */
  private savedViewRelease$: ReplaySubject<void> | null = null

  /**
   * A healthy catalog resolved before the grid existed (a synchronous `shareReplay` cache hit lands in
   * `ngOnInit`). Grid-ready then has to apply its ColDefs and bind — see {@link onGridReady}.
   */
  private healthyCatalogAwaitingGrid = false

  /**
   * True while a saved view's column types are being put back. The override change that causes is
   * the view's own state coming back, not an edit, so it must not be saved over the view.
   */
  private restoringColumnTypes = false

  constructor(
    private readonly store: WorkbenchStoreService,
    private readonly matching: MatchingService,
    private readonly aggInfo: AggregateInfoService,
    private readonly fieldCatalog: FieldCatalogService,
    private readonly dataSource: WorkbenchDataSource,
    private readonly highlighting: HighlightingService,
    private readonly filterModal: FilterModalService,
    private readonly columnTypeOverrides: ColumnTypeOverrideService,
    private readonly preferences: UserPreferencesService,
    private readonly modal: NgbModal,
    private readonly tasks: PositionsTaskService,
    private readonly toast: ToastService,
    private readonly cdr: ChangeDetectorRef,
    private readonly exporter: PositionsExportService,
    private readonly savedViews: WorkspaceGridSavedViewService,
  ) {}

  /** Clearwater exposes the Differences Only quick filter. */
  get hasQuickFilters(): boolean {
    return this.source?.hasQuickFilters === true
  }

  /**
   * Filter Groups tool panel wiring. Scoped per role so the two grids keep separate saved groups,
   * matching how this grid already registers with the Filter Modal. The external-filter accessors
   * are inert, as they are on the other Helios grids that expose this panel.
   */
  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: `${Positions2Key}-${this.role}Grid`,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  /**
   * The Panel Settings controls for the current source. Two apply everywhere; Show Closed Positions
   * is Clearwater-only, so it is driven off the registry capability flag rather than hard-coded.
   *
   * Read once when the grid is created — ag-Grid's `controls` is a static array, so a later source
   * switch does not re-open the side bar with a different list.
   */
  private panelSettingsControls(): AdvanceControl[] {
    const controls: AdvanceControl[] = [
      {
        label: 'Clear Filters on Load',
        formControl: this.clearFiltersOnLoadToggle,
      },
      {
        label: 'Sort by Absolute Value',
        formControl: this.sortByAbsoluteValueToggle,
      },
    ]
    if (this.source?.hasClosedPositionsSetting) {
      controls.push({
        label: 'Show Closed Positions',
        formControl: this.showClosedPositionsToggle,
      })
    }
    return controls
  }

  ngOnInit(): void {
    this.sourceKey = this.store.sourceValue(this.role)
    this.roleLabel = this.role === 'base' ? 'Base' : 'Comparison'
    // Seed synchronously with the computed columns so the grid renders immediately; the catalog
    // fetch then rebuilds around them.
    this.seedColumns()
    this.gridOptions = this.createGridOptions()
    this.initToolbarControls()

    // Async column pipeline: any source OR account change refetches this grid's catalog, as does a
    // user-driven Retry after a retryable (5xx) failure. switchMap drops a stale in-flight fetch
    // when a newer one supersedes it (avoids racing setColumnDefs).
    //
    // This grid is only ever created once `<hs-cache-load-listener>` (in the panel host) has seen
    // the session cache behind this role reach Done — see positions-2-panel.component.html. So
    // there is nothing to gate here: by the time this component exists, the fetch is safe to issue.
    merge(this.store.sourceFor(this.role), this.store.accountId$, this.retry$)
      .pipe(
        switchMap(() => {
          // Stop saving into the current saved view BEFORE anything below touches the grid: the
          // toolbar reset, the new catalog's setColumnDefs and the chip-default filter write are all
          // transient (or belong to the next source), and none of them may land in the view being
          // left. Re-bound in `onCatalogResolved` once a healthy catalog is in place.
          this.releaseSavedView()
          const previousSource = this.sourceKey
          this.sourceKey = this.store.sourceValue(this.role)
          this.applySourceMeta()
          // A SOURCE switch — not an account change, not a Retry — returns the toolbar to its
          // defaults and records that the new source's chip defaults still owe a write into the
          // filter model. `applyPendingSourceDefaults` performs that write once the catalog has
          // resolved, which is also what drives the single /query for the switch.
          //
          // Both happen HERE, inside the pipeline, rather than from a second subscription on the
          // same stream. A sibling subscription runs AFTER this one, and the catalog can resolve
          // SYNCHRONOUSLY — `HttpFieldCatalogService` caches per `accountId:source`, so switching
          // back to a source already loaded is a cache hit — in which case `onCatalogResolved` would
          // already have run before the flag was ever set, and the chips would be left claiming a
          // restriction the request does not carry. Ordering it explicitly is the whole point.
          //
          // `resetToolbar` is silent, so it re-drives nothing on its own.
          if (previousSource !== this.sourceKey) {
            this.resetToolbar()
            this.pendingSourceDefaults = true
          }
          return this.fetchCatalog(this.sourceKey)
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(catalog => this.onCatalogResolved(catalog))

    // The swap affordance describes what picking Enfusion here would do to the OTHER grid, so it has
    // to refresh when that grid moves — this grid's own source has not changed, so nothing else
    // would rebuild the picker and the label would go stale.
    this.store
      .sourceFor(otherRole(this.role))
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.sourceOptions = this.optionsForRole()
        this.cdr.markForCheck()
      })

    this.store
      .crossFilterFor(this.role)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ids => {
        this.crossFilterIds = ids
        // The cross-filter becomes a `rowId` set filter on the request (substituting for the
        // chip-derived matchStatus entry while active), so it has to re-drive the query rather than
        // re-run an inert client-side predicate. Clearing it restores the chip-derived entry, which is
        // the same code path — buildQueryRequest re-derives the whole filter map every time.
        if (this.source?.hasServerSideQuery) {
          this.refreshRows()
        } else {
          this.gridApi?.onFilterChanged()
          this.refreshFilterState()
        }
      })

    this.store
      .relatedGroupsForOther(this.role)
      .pipe(takeUntil(this.destroy$))
      .subscribe(groups => {
        this.relatedGroups = groups
        this.gridApi?.redrawRows()
      })

    // setSource and crossFilterToMatches reset the store's selection for BOTH roles, including the
    // grid that did not change — but ag-Grid's own selection state does not follow the store
    // automatically, so a reset to empty is applied here explicitly. Safe against feedback loops:
    // deselecting fires onSelectionChanged -> reportSelection([]), which reportSelection's setsEqual
    // guard turns into a no-op once the store is already empty.
    this.store
      .selectionFor(this.role)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ids => {
        if (ids.size === 0 && this.selectedRows().length > 0) {
          this.gridApi?.deselectAll()
        }
      })

    this.matching.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.gridApi?.refreshCells({ force: true })
      this.gridApi?.redrawRows()
      this.refreshFilterState()
    })

    // A targeted match refresh mutates the rendered row objects in place (see
    // WorkbenchStoreService.patchMatchColumns), so ag-Grid holds the same references and repaints
    // nothing on its own. Ride the same handler as a local match change — the work needed is
    // identical, and both grids listen because a patch to either source can move a match count in
    // the other.
    this.store.rowsPatched$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.gridApi?.refreshCells({ force: true })
      this.gridApi?.redrawRows()
      this.refreshFilterState()
    })

    // An AGGREGATE_INFO type override changed (from either surface — the grid-header filter or the
    // filter modal's editor row). Rebuilding the ColDefs re-stamps `filterParams.columnTypeOverride`,
    // which is what both surfaces read the current choice from; re-driving the query is what makes the
    // new `filterType` / sort `type` / aggInfo hint actually take effect.
    this.columnTypeOverrides.changes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ scope, fields, skipDestroy }) => {
        if (scope !== this.overrideScope()) return
        // Save the new types into the saved view FIRST. ag-Grid delivers the service's listeners
        // asynchronously, in the order they were queued, and its column/filter listeners save
        // whatever panelSettings the store holds when they run. Queued after the setColumnDefs (or the
        // header filter's own Apply) below, this save would let those listeners write the OLD types
        // back over it.
        if (!this.restoringColumnTypes) this.dispatchPanelSettingsChanged()
        this.colDefs = this.assembleColDefs()
        this.setColumnDefsPreservingState(this.colDefs)
        // setColumnDefs alone does NOT refresh an already-instantiated grid-header filter: this
        // ag-Grid version's IFilterComp has no `refresh(newParams)` hook, so a filter popup opened
        // BEFORE the override changed keeps showing whatever `filterParams.columnTypeOverride.type` it
        // was created with — stale until explicitly destroyed.
        //
        // `fields: null` is the wildcard a wholesale `restore` emits — every AGG_INFO column's instance
        // is destroyed there. Otherwise (a single `setType`, or a `clear` naming one or more fields)
        // ONLY the named fields are touched: an unrelated AGG_INFO column's own applied predicate must
        // survive a sibling's type change, and `skipDestroy` excludes whichever of THOSE fields is the
        // very filter instance mid-`apply()` right now — destroying it would blank the condition just
        // typed (ag-Grid disposes a filter via `setModel(null)`) before `filterChangedCallback()` gets a
        // chance to read it back out.
        const targets =
          fields ??
          this.catalogColumns
            .filter(column => column.storage === 'AGG_INFO')
            .map(column => column.field)
        targets
          .filter(field => !skipDestroy.includes(field))
          .forEach(field => this.gridApi?.destroyFilter(field))
        this.refreshRows()
      })
  }

  ngOnDestroy(): void {
    this.releaseSavedView()
    this.filterModal.destroy()
    this.retry$.complete()
    this.destroy$.next()
    this.destroy$.complete()
  }

  // ---- toolbar wiring -------------------------------------------------------

  private initToolbarControls(): void {
    // The status chips are a second face on the REAL `matchStatus` column filter, so they write that
    // filter rather than a state of their own. Wired separately from the quick filters, which stay
    // client-side external predicates on Clearwater. The Ignored chip only emits once the catalog
    // marks it enabled — see {@link updateIgnoredChipAvailability}.
    merge(
      this.includeUnmatchedFormControl.valueChanges,
      this.includeMatchedFormControl.valueChanges,
      this.includeIgnoredFormControl.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyChipsToMatchStatusFilter())

    this.differencesOnlyToggle.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onExternalFilterChanged())

    this.quickSearch.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.onSearchChanged())

    // Panel Settings persist with the saved view (PVAL-26418). The service's own panelSettings
    // listener saves the grid state together with whatever settings the event carries.
    merge(
      this.clearFiltersOnLoadToggle.valueChanges,
      this.sortByAbsoluteValueToggle.valueChanges,
      this.showClosedPositionsToggle.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.dispatchPanelSettingsChanged())
  }

  // ---- source / columns -----------------------------------------------------

  /** Seed the grid synchronously with the computed columns before the catalog resolves. */
  private seedColumns(): void {
    this.applySourceMeta()
    this.catalogColumns = []
    this.catalogGroupOrder = []
    this.colDefs = this.assembleColDefs()
  }

  /** Refresh source-derived metadata (definition + picker options). */
  private applySourceMeta(): void {
    this.source = SOURCE_REGISTRY[this.sourceKey]
    this.sourceOptions = this.optionsForRole()
  }

  /**
   * Fetch the field catalog for the current source under the focused account and adapt it. One
   * request per grid (per source); each grid fails independently. An empty catalog (e.g. a source
   * with no columns) is legitimate and resolves to an empty column list, not an error. The API 400s
   * without an accountId, so the fetch is skipped until an account is focused.
   *
   * On failure the two modes are distinguished (work item 13): a 404 (RPS has no Enfusion metadata
   * for this account's CW client) is non-retryable and offers no Retry; a 5xx or transport failure
   * is retryable. Either way the `errorId` from the Helios ReportableError envelope is surfaced so
   * a bug report can be keyed on it, and the sibling grid still renders from its own request.
   */
  private fetchCatalog(sourceKey: SourceKey) {
    const accountId = this.store.accountIdValue()
    this.columnsErrorKind = 'none'
    this.columnsErrorId = null
    this.columnsEmpty = false
    if (accountId == null) {
      this.columnsLoading = false
      return of<AdaptedCatalog>(EMPTY_CATALOG)
    }
    this.columnsLoading = true
    this.cdr.markForCheck()
    return this.fieldCatalog.getCatalog(accountId, RPS_SOURCE[sourceKey]).pipe(
      map(catalog => adaptCatalog(catalog)),
      catchError((err: unknown) => {
        // A 404/500 on this grid must not starve the sibling grid: fail this grid's columns
        // independently and fall back to the computed columns only, classifying the failure so the
        // template can offer a Retry on the retryable ones.
        const status = err instanceof HttpErrorResponse ? err.status : 0
        // A 404 is a definitive "no metadata for this client" — retrying will keep 404ing. Anything
        // else (5xx, or a status-0 transport failure) is treated as transient and retryable.
        //
        // The ticket's error table also asks for a 400 to be logged with its errorId. That is
        // deliberately not implemented here because a 400 is unreachable from this component: its
        // documented triggers are a missing accountId (the guard above early-returns before any
        // request is issued) and unparseable `sources` (always a single valid RpsSource from
        // RPS_SOURCE). If a future change ever lets a 400 reach this line it will render the
        // retryable strip and offer a Retry that keeps 400ing — revisit the row then rather than
        // adding a log with no reachable trigger now. Raised on PVAL-26146 for explicit waiver.
        this.columnsErrorKind = status === 404 ? 'not-retryable' : 'retryable'
        this.columnsErrorId = extractErrorId(err)
        this.columnsLoading = false
        return of<AdaptedCatalog>(EMPTY_CATALOG)
      }),
    )
  }

  /**
   * Retry a retryable (5xx / transport) catalog failure. Re-drives the same fetch pipeline for the
   * current source. A no-op unless the current failure is retryable, so the template only wires it
   * to the retryable state's button.
   */
  retryFetch(): void {
    if (this.columnsErrorKind !== 'retryable') return
    this.retry$.next()
  }

  /**
   * Retry just the row query after it failed.
   *
   * Re-drives the datasource rather than `retry$`: the catalog resolved fine, so re-running that
   * pipeline would re-fetch metadata that has not changed and reset the user's column layout for
   * nothing. Not offered for a `session-expired` failure — the same request cannot succeed.
   */
  retryRows(): void {
    if (this.queryFailure?.kind !== 'unknown') return
    this.refreshRows()
  }

  /**
   * The action offered alongside a 400 INVALID_FIELD_TYPE: put the named columns back to Text and
   * re-run.
   *
   * Clears the override for EVERY field the response named, not just one — Postgres reports the
   * offending value rather than the column, so the error names every field that claimed the failing
   * type and resetting a subset would let the retry fail identically. When `clear` actually removes
   * something, IT alone is enough to also drop the offending PREDICATE: it emits those fields on
   * `changes$`, and this component's own subscription (see {@link ngOnInit}) destroys exactly those
   * columns' grid-header filters — which is what evicts a `greaterThan 1000` condition left on a
   * column that is now Text, and rebuilds the ColDefs and re-drives the query. Doing either of those
   * again here would rebuild the ColDefs twice and double-fetch block 0.
   *
   * But `clear` can legitimately remove NOTHING — `failure.fields` came back empty (the 400 body's own
   * `fields` was not a list, which {@link classifyQueryError} tolerates rather than rejects), or it
   * named a field with no override at all (a POS_DATA/COLUMN field 400ing on the CATALOG's own type,
   * not an AGG_INFO one the user retyped) — and `changes$` never fires then, so nothing would re-run
   * the query at all. This still has to leave the panel usable: re-drive it directly in that case.
   */
  resetTypesToText(): void {
    const failure = this.queryFailure
    if (failure?.kind !== 'invalid-field-type') return

    const cleared = this.columnTypeOverrides.clear(
      this.overrideScope(),
      failure.fields,
    )
    this.queryFailure = null
    if (!cleared) this.refreshRows()
    this.cdr.markForCheck()
  }

  /** Apply a resolved (possibly empty) catalog to the grid. */
  private onCatalogResolved(catalog: AdaptedCatalog): void {
    this.catalogColumns = catalog.columns
    this.catalogGroupOrder = catalog.groupOrder
    this.columnsLoading = false
    this.updateIgnoredChipAvailability(catalog.columns)
    // An empty catalog is only meaningful once an account is focused; before that we simply have
    // not fetched. A focused account returning zero columns is legitimate.
    this.columnsEmpty =
      this.store.accountIdValue() != null &&
      !this.columnsError &&
      catalog.columns.length === 0
    this.colDefs = this.assembleColDefs()
    const healthy = this.isHealthyCatalog(catalog)
    if (this.gridApi) {
      this.setColumnDefsPreservingState(this.colDefs)
      // Exactly ONE of these drives the query. `applyPendingSourceDefaults` reports whether its
      // filter write already did (it does on a server-side source), in which case purging on top
      // would fetch block 0 a second time for the same source switch.
      if (!this.applyPendingSourceDefaults()) this.refreshRows()
      // Bind (and so restore) only once the live grid holds the catalog's ColDefs, or restore drops
      // every catalog colId. The restore lands a couple of microtasks later — inside the block-load
      // debounce — so a restored sort/filter folds into the query just scheduled rather than adding
      // one. Never bound on the fallback catalog (no account / fetch error / empty).
      if (healthy) this.bindSavedView()
    } else {
      this.healthyCatalogAwaitingGrid = healthy
    }
    this.cdr.markForCheck()
  }

  /** A catalog worth restoring a saved view against: focused account, fetched, non-empty. */
  private isHealthyCatalog(catalog: AdaptedCatalog): boolean {
    return (
      this.store.accountIdValue() != null &&
      this.columnsErrorKind === 'none' &&
      !this.columnsEmpty &&
      catalog.columns.length > 0
    )
  }

  // ---- workspace saved view (PVAL-26418) -------------------------------------

  /**
   * Bind this grid to its (role, source) workspace saved view: created from the current grid state
   * if missing, otherwise restored onto it, then kept up to date by the shared service's listeners
   * until {@link releaseSavedView}.
   *
   * Not awaited: the promise resolves only after the service's own `delay(100)`, past the 50 ms
   * block-load debounce, and may never resolve with no active workspace. Nothing in the grid waits
   * on it.
   */
  private bindSavedView(): void {
    if (!this.gridApi || !this.columnApi) return
    this.releaseSavedView()
    const release$ = new ReplaySubject<void>(1)
    this.savedViewRelease$ = release$
    this.savedViews
      .manageSavedView(
        { api: this.gridApi, columnApi: this.columnApi },
        savedViewGridKey(this.role, this.sourceKey),
        release$,
        true,
        this.currentPanelSettings(),
        view => {
          if (this.savedViewRelease$ === release$)
            this.restoreColumnTypeOverrides(view)
        },
      )
      .then(view => {
        // A binding released while it was still resolving is stale; its result is not ours.
        if (this.savedViewRelease$ !== release$) return
        this.applyRestoredPanelSettings(view?.panelSettings)
        // A restored filter can name IGNORED on a catalog that does not support it — the strip in
        // `updateIgnoredChipAvailability` ran before the restore, so it is repeated here.
        if (!this.ignoredSupported) this.dropUnsupportedIgnoredFromFilter()
        this.refreshFilterState()
        this.cdr.markForCheck()
      })
      .catch(() => undefined)
  }

  /** Stop the current binding's listeners. Idempotent. */
  private releaseSavedView(): void {
    this.healthyCatalogAwaitingGrid = false
    const release$ = this.savedViewRelease$
    if (!release$) return
    this.savedViewRelease$ = null
    release$.next()
    release$.complete()
  }

  private currentPanelSettings(): WorkbenchPanelSettings {
    return {
      clearFiltersOnAccountLoad: this.clearFiltersOnLoadToggle.value === true,
      sortByAbsolute: this.sortByAbsoluteValueToggle.value === true,
      showClosedPositions: this.showClosedPositionsToggle.value === true,
      columnTypeOverrides: this.columnTypeOverrides.overridesFor(
        this.overrideScope(),
      ),
    }
  }

  /**
   * Put a saved view's AGGREGATE_INFO column types back, as the shared service's `beforeRestore`
   * hook — i.e. BEFORE the view's column and filter state are applied.
   *
   * It has to come first. A restored AGG_INFO filter takes its type from the ColDef's
   * `filterParams.columnTypeOverride`, not from the saved model, so restored onto ColDefs still on
   * Text it would come back as a Text filter; and block 0 would go out with the wrong `filterType`.
   * Restoring here rebuilds the ColDefs (through the `changes$` subscription) before either can happen,
   * inside the block-load debounce.
   *
   * The view is authoritative for its source: a view saved before types were persisted, or with a
   * malformed entry, means "all Text". Skipped when the session already holds exactly these types —
   * the usual case on an account switch or a remount — since a restore destroys every AGG_INFO filter
   * and re-drives the query.
   */
  private restoreColumnTypeOverrides(view: AgGridState | undefined): void {
    const saved = (
      view?.panelSettings as Record<string, unknown> | undefined
    )?.['columnTypeOverrides']
    const restored: ColumnTypeOverrideMap = {}
    if (typeof saved === 'object' && saved !== null) {
      Object.entries(saved).forEach(([field, type]) => {
        if (isColumnTypeOverride(type)) restored[field] = type
      })
    }
    const scope = this.overrideScope()
    if (sameColumnTypes(restored, this.columnTypeOverrides.overridesFor(scope)))
      return
    this.restoringColumnTypes = true
    try {
      this.columnTypeOverrides.restore(scope, restored)
    } finally {
      this.restoringColumnTypes = false
    }
  }

  /** Patch the toggles from a restored view, silently — restoring is not a settings change. */
  private applyRestoredPanelSettings(panelSettings: unknown): void {
    if (panelSettings == null || typeof panelSettings !== 'object') return
    const settings = panelSettings as Partial<Record<string, unknown>>
    const silent = { emitEvent: false }
    if ('clearFiltersOnAccountLoad' in settings)
      this.clearFiltersOnLoadToggle.patchValue(
        Boolean(settings['clearFiltersOnAccountLoad']),
        silent,
      )
    if ('sortByAbsolute' in settings)
      this.sortByAbsoluteValueToggle.patchValue(
        Boolean(settings['sortByAbsolute']),
        silent,
      )
    if ('showClosedPositions' in settings)
      this.showClosedPositionsToggle.patchValue(
        Boolean(settings['showClosedPositions']),
        silent,
      )
  }

  /** Hand the service's panelSettings listener the new settings, while bound. */
  private dispatchPanelSettingsChanged(): void {
    if (!this.savedViewRelease$ || !this.gridApi || !this.columnApi) return
    this.gridApi.dispatchEvent(
      new CustomEvent('panelSettingsChanged', {
        detail: {
          api: { api: this.gridApi, columnApi: this.columnApi },
          panelSettings: this.currentPanelSettings(),
        },
      }),
    )
  }

  /**
   * Write a just-switched source's default toolbar state into the filter model, if one is pending.
   *
   * Deferred to here (rather than done in the source-switch subscription) so it lands AFTER the new
   * catalog is in place — a `/query` issued under the previous source's `fields[]` projection is at
   * best a wasted request and at worst a 400.
   *
   * Returns whether the write re-drove the row query on its own. It does on a server-side source:
   * ag-Grid re-runs the datasource on any filter change, which is the same property `clearAll` relies
   * on. The caller uses the answer to decide whether it still needs to purge.
   *
   * On a CLIENT-side source it does not, and `onFilterChanged` has to be called explicitly — exactly
   * as `clearAll` does for the same reason. `resetToolbar` has just returned the quick filters to
   * their defaults, and those are external-filter predicates: ag-Grid does not re-evaluate
   * `doesExternalFilterPass` off a filter-MODEL write, so without this the grid would keep showing
   * rows the (now reset) Differences Only toggle no longer selects. Unreachable while Clearwater is
   * held back from Base by the registry, and deliberately written to survive that being cleared.
   */
  private applyPendingSourceDefaults(): boolean {
    if (!this.pendingSourceDefaults) return false
    this.pendingSourceDefaults = false
    this.writeChipDefaultsIntoFilterModel()
    if (!this.source?.hasServerSideQuery) {
      this.gridApi?.onFilterChanged()
      return false
    }
    this.queryFailure = null
    return true
  }

  /**
   * Replace the WHOLE filter model with just the chip-derived `matchStatus` entry (or clear it
   * outright when the chips contribute nothing).
   *
   * Wholesale is the point here, unlike {@link applyChipsToMatchStatusFilter}'s single-column write:
   * both callers — Clear All, and a source switch — mean "drop every other column's filter too". On a
   * source switch that is not merely desirable but required, since the previous source's columns may
   * not even exist in the new catalog.
   *
   * `null` rather than an enumerated value list when every functional chip is on, for the reason
   * `matchStatusFilterModel` documents: enumerating would silently drop rows whose status this UI has
   * not enumerated.
   */
  private writeChipDefaultsIntoFilterModel(): void {
    const chipModel = this.source?.hasStatusFilter
      ? matchStatusFilterModel(this.chipState(), this.ignoredSupported)
      : null
    this.syncingChips = true
    this.gridApi?.setFilterModel(
      chipModel ? { [MATCH_STATUS_FIELD]: chipModel } : null,
    )
    this.syncingChips = false
  }

  /**
   * Re-drive the row query from row 0.
   *
   * On the server-side row model there is no "fetch the rows" step any more — every change that
   * alters WHAT the query should return (a new catalog, a source or account switch, a chip, the
   * cross-filter, an agg-info type override) purges the cache so ag-Grid re-asks the datasource for
   * block 0. `purgeServerSideCache` is ag-Grid 26's spelling.
   *
   * The store's loaded-row window is cleared alongside it, or the previous account's rows would keep
   * feeding the sibling grid's match counts until enough new blocks arrived to displace them.
   */
  private refreshRows(): void {
    this.resetLoadedRows()
    this.queryFailure = null
    this.gridApi?.purgeServerSideCache()
    this.refreshFilterState()
    this.cdr.markForCheck()
  }

  /**
   * Replace the grid's ColDefs without losing what the user has done in the Column Manager.
   *
   * `setColumnDefs` replaces the WHOLE column model — visibility, order and width included — so
   * calling it raw resets every column back to the catalog's own defaults on every account/source
   * switch, 400 recovery, or agg-info type-override Apply. None of those three triggers changes what
   * columns the user wants to see, so none of them should be able to silently undo Column Manager
   * choices. `applyColumnState` is keyed by colId: a column that no longer exists in the new ColDefs
   * is silently dropped from the reapplied state, and a newly-discovered column with no prior state
   * just keeps whatever the new ColDef says — both the safe, expected outcome.
   */
  private setColumnDefsPreservingState(colDefs: ColDef[]): void {
    const state = this.columnApi?.getColumnState()
    this.gridApi?.setColumnDefs(colDefs)
    if (state) this.columnApi?.applyColumnState({ state, applyOrder: true })
  }

  /**
   * Drop the loaded-row window.
   *
   * Blocks are keyed by start row, so stale ones do NOT get displaced by a refetch under different
   * criteria — they linger and keep feeding the sibling grid's match counts and the status-bar totals
   * rows that are no longer in the result set.
   */
  private resetLoadedRows(): void {
    this.loadedBlocks.clear()
    this.store.setRowsForSource(this.sourceKey, [])
  }

  /** Assemble the full ColDef list from the current catalog columns + runtime Aggregate Info. */
  private assembleColDefs(): ColDef[] {
    // A successful fetch that returned nothing is a real answer, surfaced by the empty strip — do
    // not paper over it with catalog columns. The computed-only fallback is for "we could not ask"
    // (no account) and "we asked and it failed" (error strip).
    if (this.columnsEmpty) {
      return buildColDefs(this.source, this.matching, [], [])
    }

    const rows = this.store.rowsForSource(this.sourceKey)
    const catalogFields = new Set(this.catalogColumns.map(c => c.field))
    const aggColumns = this.source.hasAggregateInfo
      ? this.aggInfo.discoverColumns(rows, catalogFields)
      : []
    return buildColDefs(
      this.source,
      this.matching,
      this.catalogColumns,
      aggColumns,
      this.colDefOptions(),
    )
  }

  /**
   * The build-time decisions the catalog data cannot make on its own.
   *
   * Only handed to the SERVER-SIDE sources: on Clearwater there is no /query request for a chosen
   * comparison type to travel on, so offering the type-override control there would be a control that
   * does nothing.
   */
  private colDefOptions() {
    if (!this.source?.hasServerSideQuery) return {}
    return {
      typeOverrideScope: this.overrideScope(),
      overrideFor: (field: string): ColumnTypeOverride | undefined =>
        this.columnTypeOverrides.typeFor(this.overrideScope(), field),
      columnTypeOverrideService: this.columnTypeOverrides,
    }
  }

  /**
   * Column-manager grouping (colId -> group) for the current source. Computed (survivor) columns are
   * attributed to the Helios group; catalog, calc: and Aggregate Info columns carry their own.
   */
  colGroups(): Record<string, string> {
    const rows = this.store.rowsForSource(this.sourceKey)
    const catalogFields = new Set(this.catalogColumns.map(c => c.field))
    const aggColumns = this.source.hasAggregateInfo
      ? this.aggInfo.discoverColumns(rows, catalogFields)
      : []
    return colSourceMap(
      this.source,
      this.matching,
      this.catalogColumns,
      aggColumns,
    )
  }

  /** Column Manager section order for the current source. */
  colGroupOrder(): string[] {
    return this.catalogGroupOrder
  }

  private optionsForRole(): SourceOption[] {
    return (Object.keys(SOURCE_REGISTRY) as SourceKey[])
      .filter(key => SOURCE_REGISTRY[key].allowedRoles.includes(this.role))
      .map(key => {
        const definition = SOURCE_REGISTRY[key]
        return {
          key,
          label: definition.label,
          tag: definition.tag,
          tagColor: definition.tagColor,
          disabled: !isSourceSelectable(this.role, key),
        }
      })
  }

  onSourcePicked(key: SourceKey): void {
    // The menu item is disabled, so this is belt-and-braces; the store refuses it regardless.
    if (!isSourceSelectable(this.role, key)) return
    this.store.setSource(this.role, key)
  }

  private createGridOptions(): GridOptions {
    return {
      // Server-side, not client-side: filtering and sorting now run in /query and the grid fetches
      // the next block as the user scrolls, which is what removes the old single-fixed-page cap.
      ...heliosServerSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      cacheBlockSize: CACHE_BLOCK_SIZE,
      maxBlocksInCache: MAX_BLOCKS_IN_CACHE,
      columnDefs: this.colDefs,
      // The AGGREGATE_INFO type-picker filter, referenced by name from the ColDef factory — ag-Grid 26
      // cannot type an Angular filter component as `ColDef.filter` directly.
      frameworkComponents: {
        [AGG_INFO_FILTER]: AggInfoColumnFilterComponent,
      },
      // `getRowNodeId` — NOT `getRowId`: this is ag-Grid 26, where the row-id callback is still
      // getRowNodeId and receives the row data directly.
      getRowNodeId: (data: WorkbenchRow) => data.rowId,
      context: this.gridContext,
      // Cell highlighting (right-click -> Highlight Cell) is applied here rather than per column:
      // the columns are catalog-driven, so there is no fixed list to attach it to, and no colDef in
      // this panel sets its own `cellStyle`. Spread first so the shared Helios defaults (width,
      // filterParams, tooltip) survive — assigning `defaultColDef` wholesale drops them.
      //
      // The SERVER-side defaults, to match the row model spread above. Not interchangeable with the
      // client-side ones: those add a blanket `sortable: true` / `filter: true`, which on this grid
      // would re-offer sorting and filtering on every column — including the computed and `calc:`
      // ones that `meta` keeps out of the request entirely — after PVAL-26423 deliberately moved
      // that decision per-column into the ColDef factory.
      defaultColDef: {
        ...heliosServerSideRowConfig.defaultColDef,
        cellStyle: (params: CellClassParams) => this.cellStyle(params),
      },
      // `panelSettings` is a Helios-defined iconKey, not one of ag-Grid's built-ins, so the side-bar
      // button has no glyph unless the grid supplies one. Every other Helios grid that exposes
      // Panel Settings does exactly this.
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      statusBar: workbenchStatusBarPanel,
      // The workbench Column Manager replaces ag-Grid's built-in agColumnsToolPanel (which is not
      // bundled). Rail order matches Transactions 2.0: Panel Settings, Column Manager, Filter Groups.
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: this.panelSettingsControls(),
        }),
        createWorkbenchColumnManagerToolPanel({
          columnGroups: () => this.colGroups(),
          groupOrder: () => this.colGroupOrder(),
        }),
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      // The external-filter hooks stay declared but are inert on the server-side row model: ag-Grid
      // never consults them there. They are left in place because Clearwater — which has no
      // /flex-positions endpoint — still owns the two client-side quick filters. The status chips and
      // the cross-filter are NOT here any more: the chips are the real `matchStatus` column filter, and
      // the cross-filter is a `rowId` set filter on the request.
      isExternalFilterPresent: () => externalFilterActive(this.externalModel()),
      doesExternalFilterPass: (node: RowNode) =>
        node.data
          ? passesExternalFilter(
              node.data as WorkbenchRow,
              this.externalModel(),
              row => this.hasUnitDifference(row),
            )
          : true,
      getRowClass: (params: RowClassParams) => this.rowClass(params),
      getContextMenuItems: (params: GetContextMenuItemsParams) =>
        this.getContextMenuItems(params),
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      onSelectionChanged: () => this.onSelectionChanged(),
      onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
      onFilterChanged: () => this.onGridFilterChanged(),
      onFirstDataRendered: () => this.refreshFilterState(),
      ...disableChangeDetectionOnScroll(this.cdr),
    }
  }

  /**
   * The ag-Grid server-side datasource: one `getRows` per block.
   *
   * Everything about WHAT is asked for is decided by `buildQueryRequest`, which is a pure function of
   * the grid's own filter/sort models plus the toolbar state — so the request body is testable without
   * a grid. This method owns only the parts that need the live world: which fields are visible, the
   * in-flight staleness check, and pushing the block into the store.
   *
   * Rows go into the store as well as into the grid because the matching index spans BOTH grids: a
   * match is a relationship between rows from different feeds, so the sibling grid's match counts read
   * from whatever this one has loaded. That is now a rolling window rather than a whole result set —
   * see MAX_BLOCKS_IN_CACHE and the status bar's "loaded rows" labelling.
   */
  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const sourceKey = this.sourceKey
        const accountId = this.store.accountIdValue()
        const meta = this.fieldMeta()

        // No catalog yet means no field names to project and nothing meaningful to ask for. Resolve the
        // block as a known-empty result rather than failing it, which would leave the grid in its
        // loading state forever.
        if (meta.size === 0) {
          params.successCallback([], 0)
          return
        }

        const request = buildQueryRequest({
          startRow: params.request.startRow ?? 0,
          endRow: params.request.endRow ?? CACHE_BLOCK_SIZE,
          filterModel: params.request.filterModel ?? {},
          sortModel: (params.request.sortModel ?? []) as GridSortModelEntry[],
          projection: this.projectedFields(),
          meta,
          crossFilterIds: this.crossFilterIds,
        })

        this.rowsLoading = true
        this.queryFailure = null
        this.cdr.markForCheck()

        this.dataSource
          .query(sourceKey, request)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: result => {
              this.rowsLoading = false
              // A source OR account switch may have landed while this was in flight. Fail the block
              // rather than resolving it: the grid for the NEW source has already been purged, so
              // resolving stale rows into it would show the previous account's positions.
              if (
                sourceKey !== this.sourceKey ||
                accountId !== this.store.accountIdValue()
              ) {
                params.failCallback()
                return
              }
              this.absorbBlock(request.startRow, result.rows)
              params.successCallback(result.rows, result.lastRow ?? -1)
              this.refreshFilterState()
              this.cdr.markForCheck()
            },
            error: (err: unknown) => {
              this.rowsLoading = false
              // Same staleness check as the success branch: an account/source switch that landed while
              // this was in flight means the error belongs to a grid the user has already left. Setting
              // `queryFailure` here would paint an error card over the account they are now on.
              if (
                sourceKey !== this.sourceKey ||
                accountId !== this.store.accountIdValue()
              ) {
                params.failCallback()
                return
              }
              this.queryFailure = classifyQueryError(err)
              // Resolved as an empty block (successCallback), NOT failCallback: this ag-Grid version's
              // default row renderer draws no distinction between "still pending" and "this block's
              // load failed" — a row this app never told to render any differently just keeps showing
              // ag-Grid's own "Loading" placeholder forever, underneath our own error card, which
              // renders correctly but looks like nothing happened. Resolving to zero rows ends that
              // limbo cleanly; the queryFailure card above is the actual, specific explanation, so
              // ag-Grid's own (possible) no-rows overlay underneath it is redundant, not misleading.
              // Retry still works: it purges the whole cache and re-fetches from scratch regardless of
              // whether this block's prior state was "failed" or "resolved empty".
              //
              // The SECOND argument is the total row count, not this block's size — passing the block's
              // own start row (rather than a hardcoded 0) ends the result set exactly at the failure
              // instead of collapsing every already-loaded row above it to nothing.
              params.successCallback([], request.startRow)
              this.gridApi?.hideOverlay()
              this.cdr.markForCheck()
            },
          })
      },
    }
  }

  /**
   * Keep the store's view of this source's loaded rows in step with the blocks the grid holds.
   *
   * Blocks arrive out of order and are evicted behind the scenes, so the store is rebuilt from a
   * start-row-keyed map rather than appended to — appending would accumulate duplicates of a refetched
   * block and quietly inflate every match count and total that reads it.
   */
  private absorbBlock(startRow: number, rows: WorkbenchRow[]): void {
    this.loadedBlocks.set(startRow, rows)
    const ordered = [...this.loadedBlocks.entries()]
      .sort(([a], [b]) => a - b)
      .flatMap(([, blockRows]) => blockRows)
    this.store.setRowsForSource(this.sourceKey, ordered)
  }

  /**
   * The colIds to project: the visible columns the catalog knows about, in grid order.
   *
   * Falls back to the whole catalog when the column API cannot answer (before grid-ready, or a host
   * that supplies a partial API). Projecting everything is a wider response than needed, never a wrong
   * one — where projecting NOTHING would ask the backend for a row shape with no fields at all.
   */
  private projectedFields(): string[] {
    const displayed = this.columnApi?.getAllDisplayedColumns?.()
    if (!displayed) return this.catalogColumns.map(column => column.field)
    return displayed.map(column => column.getColId())
  }

  /**
   * Per-field query metadata for the current catalog, with the user's AGGREGATE_INFO type choices
   * applied. Rebuilt per request rather than cached: the override can change between two blocks of the
   * same scroll, and a cached map would send the old type on the next block.
   */
  private fieldMeta(): ReadonlyMap<string, QueryFieldMeta> {
    return buildFieldMeta(this.catalogColumns, field =>
      this.columnTypeOverrides.typeFor(this.overrideScope(), field),
    )
  }

  /**
   * The override scope. Per panel AND per source: the same agg-info key name means different things
   * under different sources, so one shared scope would leak a Prometheus choice onto an Enfusion
   * column of the same name. NOT per role — the two grids showing the same source must agree.
   */
  private overrideScope(): string {
    return `${Positions2Key}:${this.sourceKey}`
  }

  private onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api
    this.columnApi = event.columnApi
    this.store.registerGridApi(this.role, event.api)

    // Seed the default chip state into the REAL matchStatus filter BEFORE the datasource is attached.
    // The chips no longer hold a state the request reads separately, so without this the very first
    // block would come back unfiltered — i.e. the default view would silently include matched rows.
    // Written synchronously via setFilterModel rather than the async setFilter helper, so it is
    // guaranteed to be in place by the time ag-Grid asks for block 0.
    this.seedMatchStatusFilter()

    // Attach the block datasource. Deliberately here rather than in `createGridOptions`: a datasource
    // set before the grid is ready is asked for block 0 immediately, before the catalog has resolved
    // and before `columnApi` exists to say which fields to project.
    event.api.setServerSideDatasource(this.createDatasource())

    // The catalog can resolve before the grid exists (a synchronous cache hit in ngOnInit), in which
    // case `onCatalogResolved` could not touch the grid and it is still on the seed columns. Apply
    // the catalog ColDefs now — synchronously, inside the block-load debounce the datasource just
    // started — and only then bind, so the saved view restores against the catalog's colIds.
    if (this.healthyCatalogAwaitingGrid) {
      this.healthyCatalogAwaitingGrid = false
      this.setColumnDefsPreservingState(this.colDefs)
      this.bindSavedView()
    }

    // Selection + clipboard semantics shared with every other Helios grid: click-drag and
    // ctrl-drag over cells select whole rows, ctrl-C copies the selection.
    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)

    // Row and header heights follow the user's density preference (Compact by default) so the
    // workbench matches the other panels instead of using ag-Grid's untuned defaults.
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroy$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, level === DensityLevel.Compact),
      )

    // Register with the shared Filter Modal. Scoped per role so the two grids keep separate
    // filter groups; the modal writes straight into this grid's filter model.
    this.filterModal.registerGrid(
      event.api,
      Positions2Key,
      `${Positions2Key}-${this.role}Grid`,
    )

    this.refreshFilterState()
  }

  // ---- selection + related-row tinting --------------------------------------

  private onSelectionChanged(): void {
    const ids = this.selectedRows().map(row => row.rowId)
    this.store.reportSelection(this.role, ids)
  }

  private selectedRows(): WorkbenchRow[] {
    if (!this.gridApi) return []
    return this.gridApi
      .getSelectedNodes()
      .map(node => node.data as WorkbenchRow | undefined)
      .filter((row): row is WorkbenchRow => !!row)
  }

  private rowClass(params: RowClassParams): string[] {
    const row = params.data
    if (!row) return []
    const classes: string[] = []

    // Contextual row labelling (right-click -> Highlight Row). `HighlightingService` keys off
    // `node.id`, which `getRowNodeId` makes the row id, and returns `highlighted <colour>` as one
    // space-separated string — styled globally in `apps/helios/src/styles.scss`, so no per-panel CSS
    // is needed. Its `:before` overlay paints above the related tint below, which is what makes an
    // explicit highlight win visually over an incidental one.
    const highlight = this.highlighting.getRowClass(params.node?.id ?? '')
    if (highlight) classes.push(...highlight.split(' '))

    // The cross-grid "related" tint. Selection tinting rides on ag-Grid's own `.ag-row-selected`,
    // because `getRowClass` is not re-evaluated when selection changes — rows would stay untinted
    // while click-dragging until something else forced a redraw.
    if (row.group && this.relatedGroups.has(row.group))
      classes.push('wb-related')

    return classes
  }

  /**
   * The inline style for one cell, carrying its contextual highlight.
   *
   * Keyed by `colDef.field`, the same key `HighlightingService.createHighlightMenuItems` records
   * under (`getIdentifiers` reads `column.getColDef().field`) — which is why `buildColDefs`
   * backfills `field` from `colId`. Returns `{}` for an unhighlighted cell.
   */
  private cellStyle(params: CellClassParams): CellStyle {
    return this.highlighting.getCellStyle(
      params.node?.id ?? '',
      params.colDef?.field ?? '',
    )
  }

  // ---- cell interactions ----------------------------------------------------

  private onCellClicked(event: CellClickedEvent): void {
    const colId = event.column.getColId()
    if (!event.data) return

    if (colId.startsWith(MATCH_COUNT_PREFIX)) {
      const count = Number(event.value ?? 0)
      if (!count) return
      const target = colId.slice(MATCH_COUNT_PREFIX.length) as MatchTargetKey
      const rowIds = this.matching.matchedRowIds(event.data, target)
      this.store.crossFilterToMatches(this.role, target, rowIds)
    }
  }

  // ---- context menu ---------------------------------------------------------

  /**
   * Build the right-click menu for this grid. The row half of the enablement comes from each scope
   * row's `availableActions`; the selection half (Match needs >= 2 distinct grid sources) is read
   * live off the shared store, because a match is a relationship between rows in BOTH grids and only
   * the client knows the current selection.
   */
  private getContextMenuItems(params: GetContextMenuItemsParams) {
    const scopeRows = getSelectedForContextMenu<WorkbenchRow>(params)
    return buildContextMenuItems({
      sourceKey: this.sourceKey,
      scopeRows,
      selectionSpansSources: this.store.selectionSpansSources(),
      // The clicked row, not the scope: the design's rule is about the row whose menu is open.
      matchAdditional: matchAdditionalApplies(
        params.node?.data as WorkbenchRow | undefined,
        group => this.matching.groupMembers(group),
        this.store.selectedOriginsForOther(this.role),
      ),
      onMatch: () => this.dispatchMatch(),
      onUnmatch: rows => this.dispatchUnmatch(rows),
      onIgnore: (rows, value) => this.dispatchIgnore(rows, value),
      highlightItems: this.highlighting.createHighlightMenuItems(params),
      filterOnCell: getFilterOnCellContextMenu(params),
    })
  }

  /**
   * Match / Match Additional. The scope is the CROSS-GRID selection, not this grid's menu scope: the
   * whole point of the action is to group rows from different sources, which live in the other grid.
   */
  private dispatchMatch(): void {
    const rows = this.store.allSelectedRows()
    this.dispatch('match', rows, () =>
      this.matching.match(rows.map(row => row.rowId)),
    )
  }

  private dispatchIgnore(rows: WorkbenchRow[], value: boolean): void {
    this.dispatch(value ? 'ignore' : 'unignore', rows, () =>
      this.matching.ignore(
        rows.map(row => row.rowId),
        value,
      ),
    )
  }

  /**
   * Unmatch. One other origin dissolves the group outright; two or more asks which to detach, since
   * "unmatch" is ambiguous the moment a group has more than two sides.
   */
  private dispatchUnmatch(rows: WorkbenchRow[]): void {
    const plan = planUnmatch(
      this.matching,
      rows.map(row => row.rowId),
    )
    if (plan.kind === 'none') return
    if (plan.kind === 'dissolve') {
      this.dispatch('unmatch', rows, () =>
        this.matching.unmatchGroups(plan.groups),
      )
      return
    }
    this.openUnmatchModal(rows, plan)
  }

  /**
   * The "which source do you want to detach" modal.
   *
   * ⚠ Every branch — including a single chosen source — dispatches and applies the SAME
   * whole-group dissolve. `POST /flex-positions/actions` has no detach-one-member primitive: an
   * `unmatch` deletes every match group the selection resolves to, and the request carries no
   * origin at all (helios !487). Applying {@link MatchingService.unmatchOrigin} locally would put
   * the grid into a state the server did not produce, which the next Run would silently undo, so
   * the contract wins and the chosen origin only narrows the LOCAL scope in the sense that it
   * does not — it is read for the count shown in the modal and nothing else.
   *
   * The modal is kept rather than removed because it only opens when a position is matched to two
   * or more other sources, which the current pairing (one Enfusion grid against one Prometheus
   * grid) cannot produce — so today this is an unreachable branch being held for when both the
   * pairing and a perseus detach primitive exist.
   */
  private openUnmatchModal(
    rows: WorkbenchRow[],
    plan: Extract<UnmatchPlan, { kind: 'choose' }>,
  ): void {
    const ref = this.modal.open(UnmatchModalComponent, {
      size: 'sm',
      centered: true,
    })
    const instance = ref.componentInstance as UnmatchModalComponent
    instance.title =
      rows.length > 1 ? `${rows.length} positions` : rows[0]?.sec ?? ''
    instance.sources = plan.origins.map<UnmatchSourceOption>(origin => ({
      origin,
      label: TARGET_META[origin].label,
      color: TARGET_META[origin].color,
      count: this.countOriginInGroups(plan.groups, origin),
    }))
    ref.result.then(
      () =>
        this.dispatch('unmatch', rows, () =>
          this.matching.unmatchGroups(plan.groups),
        ),
      // Dismissed (Cancel, Escape, backdrop) — nothing to unmatch, and nothing to report.
      () => undefined,
    )
  }

  private countOriginInGroups(groups: string[], origin: RowOrigin): number {
    return groups
      .flatMap(group => this.matching.groupMembers(group))
      .filter(member => member.origin === origin).length
  }

  /**
   * Post the action, then apply the local engine mutation only once the write is acknowledged.
   *
   * Order matters: the backend re-validates every action (>= 2 distinct sources, group coherence,
   * one account per group, position type), so applying the mutation first would show the user a
   * grouping the server then refused — and a refusal is now the common case rather than the
   * theoretical one, because the request actually leaves the browser.
   *
   * Two failure paths, both surfaced the same way:
   *  - **Blocked before the request.** `buildPositionActionRequest` refuses a selection that cannot
   *    produce a well-formed body (a Clearwater row, a half-sided match, an ignore over Prometheus
   *    rows). Nothing is posted; the reason it wrote is shown as-is.
   *  - **Rejected by the endpoint.** A 400 carries `[{ validationId, message }]`, which
   *    `positionActionErrorMessage` turns into one sentence. A transport failure — including the
   *    404 this gets until helios !487/!491 deploy — falls through to the same generic sentence.
   *
   * `takeUntil(destroy$)` so a panel closed mid-flight does not mutate a store it has left behind.
   */
  private dispatch(
    action: PositionActionKey,
    rows: WorkbenchRow[],
    apply: () => void,
  ): void {
    this.actionError = false

    const plan = buildPositionActionRequest(action, rows)
    if (plan.kind === 'blocked') {
      this.failAction(plan.reason, 'warning')
      return
    }

    this.tasks
      .submit(plan.request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          apply()
          this.cdr.markForCheck()
        },
        error: (err: unknown) =>
          this.failAction(positionActionErrorMessage(action, err), 'error'),
      })
  }

  /** Record and surface a refused action. The grids are left exactly as they were. */
  private failAction(message: string, level: 'warning' | 'error'): void {
    this.actionError = true
    if (level === 'warning') this.toast.warning(message)
    else this.toast.error(message)
    this.cdr.markForCheck()
  }

  // ---- filters --------------------------------------------------------------

  private externalModel(): WorkbenchExternalFilterModel {
    return {
      quick: this.hasQuickFilters ? this.quickFilterState() : null,
    }
  }

  private chipState(): MatchStatusChipState {
    return {
      matched: this.includeMatchedFormControl.value === true,
      unmatched: this.includeUnmatchedFormControl.value === true,
      ignored: this.includeIgnoredFormControl.value === true,
    }
  }

  /**
   * Put the current chip state into the `matchStatus` filter model synchronously.
   *
   * Only used for the initial seed, where the whole filter model is still empty so replacing it
   * wholesale is safe. Every later chip change goes through {@link applyChipsToMatchStatusFilter},
   * which touches only this one column's filter and leaves the user's other filters alone.
   */
  private seedMatchStatusFilter(): void {
    if (!this.gridApi || !this.source?.hasStatusFilter) return
    const chipModel = matchStatusFilterModel(
      this.chipState(),
      this.ignoredSupported,
    )
    if (!chipModel) return
    this.syncingChips = true
    this.gridApi.setFilterModel({ [MATCH_STATUS_FIELD]: chipModel })
    this.syncingChips = false
  }

  /**
   * Chips -> filter. Writes the chip state into the real `matchStatus` column filter.
   *
   * Uses the house {@link setFilter} helper, which is also how Positions 1.0 drives its status filter
   * from a toolbar toggle: it sets the model (or DESTROYS the filter when handed `null`) and then calls
   * `onFilterChanged`, which is what re-drives the server-side query.
   *
   * `null` — every functional chip on — clears the filter rather than enumerating every value, so no
   * `matchStatus` entry reaches the request at all. Enumerating instead would silently drop rows whose
   * status is absent or is a value this UI has not enumerated.
   */
  private applyChipsToMatchStatusFilter(): void {
    if (!this.gridApi || !this.source?.hasStatusFilter) return
    this.syncingChips = true
    void setFilter<Record<string, unknown>>(
      MATCH_STATUS_FIELD,
      matchStatusFilterModel(this.chipState(), this.ignoredSupported),
      this.gridApi,
    ).finally(() => {
      this.syncingChips = false
    })
  }

  /**
   * Filter -> chips. Keeps the checkboxes truthful when the `matchStatus` filter is changed by some
   * OTHER surface — the column's own header filter, or the Filters modal.
   *
   * Skipped while {@link applyChipsToMatchStatusFilter} is mid-flight: that direction already knows the
   * chip state, and re-deriving it there would just be a no-op round trip.
   */
  private syncChipsFromMatchStatusFilter(): void {
    if (!this.source?.hasStatusFilter || this.syncingChips) return
    const model = readMatchStatusFilterModel(
      this.gridApi?.getFilterInstance(MATCH_STATUS_FIELD)?.getModel(),
    )
    const chips = chipStateFromFilterModel(model)
    const silent = { emitEvent: false }
    this.includeMatchedFormControl.setValue(chips.matched, silent)
    this.includeUnmatchedFormControl.setValue(chips.unmatched, silent)
    // Written even while disabled (an unsupported catalog): harmless, since matchStatusFilterModel
    // never trusts this control's raw value for anything when ignoredSupported is false.
    this.includeIgnoredFormControl.setValue(chips.ignored, silent)
    this.cdr.markForCheck()
  }

  /**
   * Any filter changed, from any surface.
   *
   * On a server-side source the loaded-row window is dropped: ag-Grid re-drives the query itself on a
   * filter change, but blocks fetched under the OLD filter are keyed by start row and would otherwise
   * linger in the store, inflating the sibling grid's match counts and the status-bar totals.
   */
  private onGridFilterChanged(): void {
    this.syncChipsFromMatchStatusFilter()
    if (this.source?.hasServerSideQuery) this.resetLoadedRows()
    this.refreshFilterState()
  }

  private quickFilterState(): QuickFilterState {
    return {
      differencesOnly: this.differencesOnlyToggle.value === true,
    }
  }

  /** Whether either unit-difference column is non-zero for this row. */
  private hasUnitDifference(row: WorkbenchRow): boolean {
    const custody = calcUnitValue('custUnitsDiff', row, this.matching)
    const trading = calcUnitValue('tradUnitsDiff', row, this.matching)
    return (
      (custody != null && custody !== 0) || (trading != null && trading !== 0)
    )
  }

  /**
   * A quick-filter toggle changed (Clearwater only).
   *
   * The status chips do NOT come through here any more — they write the real `matchStatus` column
   * filter, so ag-Grid's own filter-changed path re-drives the query for them.
   */
  onExternalFilterChanged(): void {
    if (this.source?.hasServerSideQuery) {
      this.refreshRows()
      return
    }
    this.gridApi?.onFilterChanged()
    this.refreshFilterState()
  }

  onSearchChanged(): void {
    // Quick Search is disabled on the server-side sources (see quickSearchDisabledReason): a quick
    // filter can only match the rows currently loaded, so applying it would silently present a
    // rolling window's matches as the whole result. Guarded here as well as in the template so a
    // programmatic set cannot reintroduce it.
    if (this.source?.hasServerSideQuery) return
    this.gridApi?.setQuickFilter(this.quickSearch.value ?? '')
    this.refreshFilterState()
  }

  /**
   * Clear All Filters returns the grid to its DEFAULT view, which on Prometheus is unmatched-only —
   * not "no filters at all".
   *
   * The chips are the `matchStatus` filter now, so the default chip state has to be written back INTO
   * the filter model rather than left cleared; otherwise the chips would claim a restriction the
   * request does not carry. Done as one `setFilterModel` call because that re-drives the server-side
   * query itself — clearing and then re-applying would fetch block 0 twice.
   */
  clearAll(): void {
    this.resetToolbar()
    this.quickSearch.reset('', { emitEvent: false })
    if (!this.source?.hasServerSideQuery) this.gridApi?.setQuickFilter('')
    this.store.clearCrossFilter(this.role)

    this.writeChipDefaultsIntoFilterModel()

    if (!this.source?.hasServerSideQuery) this.gridApi?.onFilterChanged()
    this.refreshFilterState()
  }

  /**
   * Return every toolbar filter to its default. Silent so a reset triggers a single grid refresh
   * from the caller rather than one per control. Ignored resets to off, same as Matched — the default
   * view is Unmatched-only, whether or not Ignored is even supported on this catalog.
   */
  private resetToolbar(): void {
    const silent = { emitEvent: false }
    this.includeUnmatchedFormControl.setValue(true, silent)
    this.includeMatchedFormControl.setValue(false, silent)
    this.includeIgnoredFormControl.setValue(false, silent)
    this.differencesOnlyToggle.setValue(false, silent)
  }

  /**
   * Enable or disable the Ignored chip for the catalog that just resolved.
   *
   * `matchStatus`'s `allowedValues` is the one source of truth for whether this account's backend
   * represents "ignored" yet (PVAL-26415) — checked fresh on every catalog resolution rather than
   * assumed fixed, since different sources/accounts can be on different backend versions at the same
   * time. Disabling it also forces it back to `false` (never left checked-but-inert) so a stale
   * checked state from a previously-supported source cannot linger onto one that isn't.
   */
  private updateIgnoredChipAvailability(
    catalogColumns: ColumnCatalogEntry[],
  ): void {
    const matchStatusEntry = catalogColumns.find(
      column => column.field === MATCH_STATUS_FIELD,
    )
    this.ignoredSupported =
      matchStatusEntry?.allowedValues?.includes('IGNORED') ?? false

    if (this.ignoredSupported) {
      this.includeIgnoredFormControl.enable({ emitEvent: false })
      return
    }

    this.includeIgnoredFormControl.setValue(false, { emitEvent: false })
    this.includeIgnoredFormControl.disable({ emitEvent: false })
    this.dropUnsupportedIgnoredFromFilter()
  }

  /**
   * Strip a stale `IGNORED` value out of the live `matchStatus` filter when a catalog switch drops
   * Ignored support.
   *
   * Disabling the chip above only updates the CONTROL — a value checked against the previous
   * (supporting) account's filter model survives untouched otherwise, so the next request would
   * still enumerate `IGNORED` against a backend that does not recognise it. Only touches the grid
   * when the live model actually names it, so the common case (nothing to correct) costs nothing
   * extra; the rare case (support just flipped off while Ignored was checked) accepts one extra
   * re-fetch on top of the `refreshRows()` the catalog switch already triggers — the same trade-off
   * `clearAll()`'s own double-write already makes.
   */
  private dropUnsupportedIgnoredFromFilter(): void {
    if (!this.gridApi || !this.source?.hasStatusFilter) return
    const model = readMatchStatusFilterModel(
      this.gridApi.getFilterInstance(MATCH_STATUS_FIELD)?.getModel(),
    )
    if (!model?.values?.includes('IGNORED')) return

    const corrected = matchStatusFilterModel(
      this.chipState(),
      this.ignoredSupported,
    )
    // The single-column idiom, not a wholesale setFilterModel: this touches ONLY the matchStatus
    // column, the same way applyChipsToMatchStatusFilter does — a wholesale write would drop every
    // OTHER column's applied filter, and clear the whole grid outright when `corrected` is null.
    this.syncingChips = true
    void setFilter<Record<string, unknown>>(
      MATCH_STATUS_FIELD,
      corrected,
      this.gridApi,
    ).finally(() => {
      this.syncingChips = false
    })
  }

  /**
   * Keep the applied-filter bubble and the Clear-all button in step with the live filter state.
   *
   * The status chips are counted by `filterCount` like any other column filter, because they ARE the
   * `matchStatus` column's filter — there is no separate chip state to add on top, and adding one
   * would double-count the same restriction.
   */
  private refreshFilterState(): void {
    this.filterCount = Object.keys(this.gridApi?.getFilterModel() ?? {}).length
    this.clearAllFiltersEnabled =
      this.filterCount > 0 ||
      !!this.quickSearch.value ||
      this.crossFilterIds != null ||
      this.isQuickFilterModified()
    this.cdr.markForCheck()
  }

  private isQuickFilterModified(): boolean {
    return this.quickFilterState().differencesOnly
  }

  // ---- toolbar actions ------------------------------------------------------

  openFilters(): void {
    void this.filterModal.launchFiltersModal()
  }

  /**
   * Server-side "Export to Excel" (PVAL-26421): POST /flex-positions/export renders the rows the grid
   * is showing — same filter, status chips, cross-filter and sort — in the displayed catalog columns,
   * and the returned `.xlsx` Blob is downloaded as `positions-2-<source>.xlsx`. The filename is set
   * here because a Blob response exposes no `Content-Disposition` to read one from.
   *
   * A no-op before the grid is ready (there is no live state to export yet) and while an earlier
   * export is still in flight. Clearwater has no export endpoint, and a grid with no exportable
   * column would only earn a 400, so both get a warning and send nothing.
   */
  exportToExcel(): void {
    if (this.exportInProgress || !this.gridApi || !this.columnApi) return

    const request = this.source?.hasServerSideQuery
      ? buildExportRequest({
          filterModel: this.gridApi.getFilterModel() ?? {},
          sortModel: sortModelFromColumnState(
            this.columnApi.getColumnState?.(),
          ),
          displayed: (this.columnApi.getAllDisplayedColumns?.() ?? []).map(
            column => column.getColId(),
          ),
          catalog: this.catalogColumns,
          meta: this.fieldMeta(),
          crossFilterIds: this.crossFilterIds,
          userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      : null

    if (!request) {
      this.toast.warning(EXPORT_MESSAGES.nothingToExport, EXPORT_TOAST_CONFIG)
      return
    }

    // Captured now: a source switch while the export runs must not rename the file it produces.
    const sourceKey = this.sourceKey
    this.exportInProgress = true
    this.cdr.markForCheck()
    this.toast.warning(
      EXPORT_MESSAGES.inProgress,
      EXPORT_TOAST_CONFIG,
      EXPORT_MESSAGES.inProgressTitle,
    )

    this.exporter
      .exportToExcel(sourceKey, request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.exportInProgress = false
          this.cdr.markForCheck()
        }),
      )
      .subscribe({
        next: file => {
          downloadExcelFile(file, `positions-2-${sourceKey}`)
          this.toast.success(EXPORT_MESSAGES.success, EXPORT_TOAST_CONFIG)
        },
        error: () => {
          this.toast.error(EXPORT_MESSAGES.failed, '', EXPORT_TOAST_CONFIG)
        },
      })
  }
}
